/**
 * GT IO review — "Creative assignment": Upload / Remove Creatives status and
 * projected assignments.
 *
 * Renders three things, all from data the gt-api worker produced
 * (gt_api/gt_creative_upload.py) — none of the matching happens here:
 *
 *   1. the in-flight upload: transfer %, then "processing";
 *   2. the outcome of the last upload: counts (imported / assigned /
 *      unmatched / skipped / invalid), warnings and blocking errors traced to
 *      their sheet, row, asset or ad group, and — for a stale-revision
 *      conflict — a reload action;
 *   3. the ACTIVE bundle's projected assignments by ad group and creative
 *      size, recomputed by the worker on every re-validation, so they follow
 *      the plan as it is edited.
 *
 * Blocking findings for the active bundle also sit in ctsReview.errors, which
 * is what disables Approve & launch; this panel explains them.
 */

import { useState } from 'react';

const COUNT_LABELS = [
  ['imported', 'Imported'],
  ['assigned', 'Assigned'],
  ['unmatched', 'Unmatched'],
  ['skipped', 'Skipped'],
  ['invalid', 'Invalid'],
];

function where(issue) {
  return ['sheet', 'row', 'asset', 'adgroup', 'size']
    .map((k) => issue?.[k])
    .filter((v) => v !== undefined && v !== null && v !== '')
    .join(' · ');
}

/** Pure view model over the envelope. */
export function summarizeCreativeUpload(ctsReview) {
  const meta = ctsReview?.result?._creative_upload || null;
  // A plan that no longer carries an upload (creatives removed, or reverted to
  // a version from before one) has nothing to show, whatever report an older
  // envelope still holds.
  if (ctsReview?.result && !meta) return null;
  const report = ctsReview?.creative_upload || meta?.report || null;
  if (!report && !meta) return null;
  const projection = report?.projection || [];
  const issues = Array.isArray(report?.issues) ? report.issues : [];
  return {
    sourceFilename: report?.source_filename || meta?.source_filename || null,
    checksum: report?.checksum_sha256 || meta?.checksum_sha256 || null,
    uploadedBy: report?.uploaded_by || meta?.uploaded_by || null,
    uploadedAt: report?.uploaded_at || meta?.uploaded_at || null,
    parserVersion: report?.parser_version || meta?.parser_version || null,
    parentRevision: report?.parent_revision || meta?.parent_revision || null,
    counts: report?.counts || {},
    assets: meta?.assets || report?.assets || [],
    warnings: issues.filter((i) => i.severity === 'warning'),
    infos: issues.filter((i) => i.severity === 'info'),
    projection: projection.filter((row) => (row.sizes || []).length > 0),
  };
}

function Issues({ items, tone }) {
  return (
    <ul className="clw-cu-list">
      {items.map((i, n) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={n} className={tone}>
          {where(i) && <span className="clw-cu-src">{where(i)}</span>}
          <span>{i.message || String(i)}</span>
        </li>
      ))}
    </ul>
  );
}

export function GtCreativeUploadPanel({ ctsReview, upload = {}, onReload = null }) {
  const [open, setOpen] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const vm = summarizeCreativeUpload(ctsReview);
  const hasOutcome = upload.busy || upload.error || (upload.errors?.length > 0) || upload.stale || upload.note;
  if (!vm && !hasOutcome) return null;

  return (
    <div className={`clw-cu-panel ${upload.errors?.length || upload.stale ? 'blocked' : ''}`} data-testid="gt-creative-upload-panel">
      <div className="clw-cu-head">
        <div className="clw-cu-title">
          Creative assignment
          <span className="clw-cu-sub">
            {upload.busy
              ? (upload.phase === 'processing' ? 'Processing the workbook…' : `Uploading… ${upload.percent || 0}%`)
              : vm?.sourceFilename
                ? `${vm.sourceFilename}${vm.uploadedAt ? ` · ${new Date(vm.uploadedAt).toLocaleString()}` : ''}`
                : 'No creatives uploaded yet'}
          </span>
        </div>
        <button type="button" className="clw-issues-toggle" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide ▲' : 'Show ▼'}
        </button>
      </div>
      {open && (
        <div className="clw-cu-body">
          {upload.busy && (
            <div className="clw-cu-progress" role="progressbar" aria-valuenow={upload.phase === 'processing' ? 100 : (upload.percent || 0)} aria-valuemin={0} aria-valuemax={100}>
              <div className={`clw-cu-bar ${upload.phase === 'processing' ? 'indeterminate' : ''}`} style={{ width: `${upload.phase === 'processing' ? 100 : (upload.percent || 0)}%` }} />
            </div>
          )}
          {upload.stale && (
            <div className="clw-cu-error">
              {upload.error || 'This configuration changed since you loaded it.'} Nothing was changed.
              {onReload && (
                <button type="button" className="clw-cu-ack" onClick={onReload}>Reload current revision</button>
              )}
            </div>
          )}
          {!upload.stale && upload.error && <div className="clw-cu-error">{upload.error}</div>}
          {upload.errors?.length > 0 && (
            <div className="clw-cu-section err">
              <div className="clw-cu-section-title">
                Upload not applied — correct the CTS and upload again <span className="n">{upload.errors.length}</span>
              </div>
              <Issues items={upload.errors} tone="" />
            </div>
          )}
          {upload.note && <div className="clw-cu-note">{upload.note}</div>}

          {vm && (
            <>
              <div className="clw-cu-counts">
                {COUNT_LABELS.map(([k, label]) => (
                  <div key={k} className={`clw-cu-count ${k}`}>
                    <div className="v">{vm.counts?.[k] ?? 0}</div>
                    <div className="l">{label}</div>
                  </div>
                ))}
              </div>
              {vm.warnings.length > 0 && (
                <div className="clw-cu-section warn">
                  <div className="clw-cu-section-title">Warnings <span className="n">{vm.warnings.length}</span></div>
                  <Issues items={vm.warnings} tone="" />
                </div>
              )}
              {vm.projection.length > 0 && (
                <div className="clw-cu-section">
                  <div className="clw-cu-section-title">Projected assignments by ad group and size <span className="n">{vm.projection.length}</span></div>
                  <table className="clw-cu-table" data-testid="gt-creative-projection">
                    <thead><tr><th>Ad group</th><th>Size</th><th>Creative / tag</th><th>Trackers</th></tr></thead>
                    <tbody>
                      {vm.projection.flatMap((row) => (row.sizes || []).map((slot, n) => (
                        <tr key={`${row.index}-${slot.size}`} className={slot.creatives?.length || slot.tags?.length ? '' : 'unassigned'}>
                          {n === 0 && <td rowSpan={row.sizes.length}>{row.ad_group_name}</td>}
                          <td>{slot.size}</td>
                          <td>
                            {[...(slot.creatives || []), ...(slot.tags || []).map((t) => `tag ${t}`)].join(', ') || '— unassigned'}
                            {(slot.hosted_creatives || []).filter((entry) => Object.keys(entry.payload || {}).length).map((entry) => (
                              <details key={entry.creative_name}>
                                <summary>{entry.creative_name}: destination and distance overlay</summary>
                                {entry.adgroup_creative_settings && <div>Ad-group defaults: {entry.adgroup_creative_settings}</div>}
                                <div>Destination: {entry.payload.landing_page ? 'GT Super Landing Page' : entry.payload.click_through_url || 'External URL'}</div>
                                {entry.payload.landing_page && <>
                                  <div>Header: {entry.assets?.header} — {entry.payload.landing_page.header_click_through_url}</div>
                                  {entry.assets?.coupon && <div>Promo: {entry.assets.coupon} — {entry.payload.landing_page.coupon_click_through_url}</div>}
                                  <div>Directions label: {entry.payload.landing_page.directions_button_label ?? 'None'}</div>
                                  <div>Call label: {entry.payload.landing_page.call_button_label ?? 'None'}</div>
                                  <div>Additional button: {entry.payload.landing_page.additional_button_text ?? 'None'} — {entry.payload.landing_page.additional_button_click_through_url}</div>
                                </>}
                                {entry.payload.image_banner_text_overlay && <>
                                  <div>Distance text: {entry.payload.image_banner_text_overlay}</div>
                                  <div>Placement and style: {entry.payload.image_banner_text_overlay_css}</div>
                                </>}
                                {entry.override_reason && <div>Override: {entry.override_reason}</div>}
                              </details>
                            ))}
                          </td>
                          <td>
                            {[
                              ...(slot.trackers || []),
                              ...(slot.viewability ? [`${slot.viewability} viewability`] : []),
                              ...(row.impression_pixels ? [`${row.impression_pixels} pixel${row.impression_pixels === 1 ? '' : 's'}`] : []),
                            ].join(', ') || '—'}
                          </td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              )}
              {vm.infos.length > 0 && (
                <div className="clw-cu-original">
                  <button type="button" className="clw-cu-link" onClick={() => setShowInfo((v) => !v)}>
                    {showInfo ? 'Hide' : 'Show'} {vm.infos.length} note{vm.infos.length === 1 ? '' : 's'} (ignored sheets, skipped files)
                  </button>
                  {showInfo && <Issues items={vm.infos} tone="" />}
                </div>
              )}
              <div className="clw-cu-meta">
                {[
                  vm.parserVersion,
                  vm.checksum && `sha256 ${vm.checksum.slice(0, 12)}…`,
                  vm.uploadedBy && `by ${vm.uploadedBy}`,
                  vm.assets?.length ? `${vm.assets.length} asset${vm.assets.length === 1 ? '' : 's'} stored` : null,
                  vm.parentRevision && `based on ${vm.parentRevision.split('/').pop()}`,
                ].filter(Boolean).join(' · ')}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default GtCreativeUploadPanel;
